import { useMemo, useReducer } from 'react';
import '../../styles/Calculator.css';
import { frequencies, addOnRates, baseRate, minCharge, initialModel } from './model';
import { update } from './update';
import { computeResult, toContactUrl } from './domain';

function CalculatorView() {
  const [model, dispatch] = useReducer(update, initialModel);

  const result = useMemo(() => computeResult(model), [model]);

  const handleContact = (calc) => {
    const url = toContactUrl(model, calc);
    window.location.href = url;
  };

  return (
    <section className="calculator-section" id="calculator" aria-labelledby="calculator-title">
      <div className="calculator-shell">
        <div className="calculator-header">
          <span className="calculator-kicker">Kalkyl & detaljer</span>
          <h2 id="calculator-title">Räkna ut ditt pris</h2>
          <p>Välj privat eller företag, ange yta och tillval. Vi visar ett indikativt pris och RUT för privat.</p>
        </div>

        <div className="calculator-grid">
          <div className="calculator-card">
            <div className="mode-toggle" role="group" aria-label="Välj kundtyp">
              <button
                type="button"
                className={`chip ${model.mode === 'privat' ? 'active' : ''}`}
                onClick={() => dispatch({ type: 'setMode', value: 'privat' })}
                aria-pressed={model.mode === 'privat'}
              >
                Privat (inkl. RUT)
              </button>
              <button
                type="button"
                className={`chip ${model.mode === 'foretag' ? 'active' : ''}`}
                onClick={() => dispatch({ type: 'setMode', value: 'foretag' })}
                aria-pressed={model.mode === 'foretag'}
              >
                Företag (ex moms)
              </button>
            </div>

            <div className="field-row">
              <label htmlFor="area">Yta (kvm)</label>
              <input
                id="area"
                type="number"
                min="0"
                value={model.area}
                onChange={(e) => dispatch({ type: 'setArea', value: e.target.value })}
                placeholder="t.ex. 150"
              />
            </div>

            <div className="field-row">
              <label>Frekvens</label>
              <div className="frequency-select" aria-label="Välj frekvens (mobil)">
                <select
                  value={model.frequency}
                  onChange={(e) => dispatch({ type: 'setFrequency', value: e.target.value })}
                >
                  {frequencies.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label} · {f.desc}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pill-row" role="radiogroup" aria-label="Välj frekvens">
                {frequencies.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`pill ${model.frequency === f.id ? 'active' : ''}`}
                    onClick={() => dispatch({ type: 'setFrequency', value: f.id })}
                    aria-pressed={model.frequency === f.id}
                  >
                    <span>{f.label}</span>
                    <small>{f.desc}</small>
                  </button>
                ))}
              </div>
            </div>

            <div className="field-row">
              <label>Tillval</label>
              <div className="addon-grid">
                <label className={`addon ${model.addOns.windows ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={model.addOns.windows}
                    onChange={() => dispatch({ type: 'toggleAddOn', key: 'windows' })}
                  />
                  <div>
                    <span>Fönsterputs</span>
                    <small>+{addOnRates.windows} kr/kvm</small>
                  </div>
                </label>
                <label className={`addon ${model.addOns.floors ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={model.addOns.floors}
                    onChange={() => dispatch({ type: 'toggleAddOn', key: 'floors' })}
                  />
                  <div>
                    <span>Golvvård</span>
                    <small>+{addOnRates.floors} kr/kvm</small>
                  </div>
                </label>
                <label className={`addon ${model.addOns.deep ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={model.addOns.deep}
                    onChange={() => dispatch({ type: 'toggleAddOn', key: 'deep' })}
                  />
                  <div>
                    <span>Storstäd</span>
                    <small>+{addOnRates.deep} kr/kvm</small>
                  </div>
                </label>
              </div>
            </div>

            <div className="field-row loyalty-row">
              <label>
                <input
                  type="checkbox"
                  checked={model.loyalty}
                  onChange={() => dispatch({ type: 'toggleLoyalty' })}
                  disabled={model.frequency !== 'weekly'}
                />
                <span>Lojalitetsprogram: 10% rabatt vid veckovis städ, 12 mån bindning</span>
              </label>
              {model.frequency !== 'weekly' && <small className="hint">Aktiveras vid frekvens "Varje vecka".</small>}
            </div>
          </div>

          <div className="calculator-card result-card" aria-live="polite">
            <div className="result-header">
              <span className="badge">{model.mode === 'privat' ? 'Privat (inkl. RUT)' : 'Företag (ex moms)'}</span>
              {result.loyaltyEligible && <span className="badge success">-10% lojalitet</span>}
            </div>

            <div className="result-figure">
              <p className="result-total">{Math.round(result.total)} kr</p>
              <p className="result-sub">ca {result.perKvm} kr/kvm · {result.selectedFreq.label}</p>
            </div>

            <div className="breakdown">
              <div>
                <span>Grund (yta × {baseRate[model.mode]} kr × {result.selectedFreq.multiplier}x)</span>
                <strong>{Math.round(result.baseCost)} kr</strong>
              </div>
              <div>
                <span>Tillval</span>
                <strong>{Math.round(result.addOnCost)} kr</strong>
              </div>
              {result.loyaltyEligible && (
                <div>
                  <span>Lojalitetsrabatt 10%</span>
                  <strong>-{Math.round(result.subtotal * 0.1)} kr</strong>
                </div>
              )}
              {model.mode === 'privat' && (
                <div>
                  <span>RUT-avdrag (50%)</span>
                  <strong>-{Math.round(result.rutDeduction)} kr</strong>
                </div>
              )}
              <div>
                <span>Minimidebitering</span>
                <strong>{minCharge[model.mode]} kr</strong>
              </div>
            </div>

            <p className="disclaimer">Pris är indikativt. Vi bekräftar efter platsbesök och specificerade behov.</p>

            <button
              type="button"
              className="contact-cta"
              onClick={() => handleContact(result)}
            >
              Kontakta oss Idag
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CalculatorView;
